(() => {
  const rootSelector = '[data-nova-mobile-navigation]';
  const controllers = new Map();
  const arrowPath = 'M9 5l7 7-7 7';

  function element(tag, className, text) {
    const node = document.createElement(tag);
    if (className) node.className = `nova-mobile-navigation__${className}`;
    if (text !== undefined) node.textContent = text;
    return node;
  }

  function arrow() {
    const svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
    svg.setAttribute('viewBox', '0 0 24 24');
    svg.setAttribute('width', '20');
    svg.setAttribute('height', '20');
    svg.setAttribute('fill', 'none');
    svg.setAttribute('aria-hidden', 'true');
    svg.setAttribute('focusable', 'false');
    const path = document.createElementNS(svg.namespaceURI, 'path');
    path.setAttribute('d', arrowPath);
    path.setAttribute('stroke', 'currentColor');
    path.setAttribute('stroke-width', '1.8');
    path.setAttribute('stroke-linecap', 'round');
    path.setAttribute('stroke-linejoin', 'round');
    svg.append(path);
    return svg;
  }

  function destination(href) {
    if (!href || href.trim() === '#') return false;
    try {
      return ['http:', 'https:', 'mailto:', 'tel:'].includes(new URL(href, location.href).protocol);
    } catch {
      return false;
    }
  }

  function readList(list, depth = 1) {
    if (!list || depth > 3) return [];
    return Array.from(list.children).filter((node) => node.tagName === 'LI').flatMap((item) => {
      const labelNode = item.querySelector(':scope > a, :scope > button.nova-navigation-parent, :scope > details > summary');
      if (!labelNode) return [];
      const label = (labelNode.querySelector('[data-hover]')?.dataset.hover || labelNode.textContent).trim().replace(/\s+/g, ' ');
      if (!label) return [];
      const link = labelNode.matches('a') ? labelNode : Array.from(item.querySelectorAll('a')).find((node) => {
        return node.closest('li') === item && !node.closest('.sub-menu-block');
      });
      const childList = Array.from(item.querySelectorAll('ul')).find((node) => node.closest('li') === item);
      return [{
        label,
        href: link?.getAttribute('href') || '',
        current: link?.getAttribute('aria-current') === 'page',
        target: link?.getAttribute('target') || '',
        rel: link?.getAttribute('rel') || '',
        children: readList(childList, depth + 1)
      }];
    });
  }

  function distinctMenus(lists) {
    const signatures = new Set();
    return lists.flatMap((list) => {
      const entries = readList(list);
      const signature = JSON.stringify(entries);
      if (!entries.length || signatures.has(signature)) return [];
      signatures.add(signature);
      return entries;
    });
  }

  class NovaMobileNavigation {
    constructor(root) {
      this.root = root;
      if (root.parentElement !== document.body) document.body.append(root);
      this.drawer = root.querySelector('.nova-mobile-navigation__drawer');
      this.body = root.querySelector('[data-nova-mobile-body]');
      this.rootPanel = root.querySelector('[data-nova-mobile-panel="root"]');
      this.rootList = root.querySelector('[data-nova-mobile-root-list]');
      this.closeButton = root.querySelector('button[data-nova-mobile-close]');
      this.key = root.dataset.menuKey || 'dt-main-menu';
      this.mobile = matchMedia('(max-width: 1279px)');
      this.motion = matchMedia('(prefers-reduced-motion: reduce)');
      this.abort = new AbortController();
      this.isOpen = false;
      this.stack = [];
      this.panels = new Map();
      this.triggerAttributes = new Map();
      this.background = new Map();
      this.serial = 0;
      this.refresh();
      this.listen(document, 'click', (event) => this.onDocumentClick(event), true);
      this.listen(document, 'keydown', (event) => this.onKeyDown(event), true);
      this.listen(document, 'focusin', (event) => {
        if (this.isOpen && !this.drawer.contains(event.target)) this.closeButton.focus({ preventScroll: true });
      }, true);
      this.listen(root, 'click', (event) => this.onDrawerClick(event));
      this.listen(this.mobile, 'change', () => {
        if (!this.mobile.matches) this.close(false);
        this.syncTriggers();
      });
      this.listen(this.motion, 'change', () => {
        if (!this.motion.matches) return;
        const complete = this.motionComplete;
        this.motionComplete = null;
        this.cancelDrawerMotion();
        this.panelAnimation?.cancel();
        complete?.();
      });
    }

    listen(target, type, callback, capture = false) {
      target.addEventListener(type, callback, { signal: this.abort.signal, capture });
    }

    triggers() {
      return Array.from(document.querySelectorAll('.menu-trigger[data-menu], #header summary.header__icon--menu')).filter((trigger) => {
        if (trigger.matches('.menu-trigger')) return trigger.dataset.menu === this.key;
        return this.key === 'dt-main-menu';
      });
    }

    syncTriggers() {
      this.triggers().forEach((trigger) => {
        if (!this.triggerAttributes.has(trigger)) {
          const attributes = ['aria-controls', 'aria-expanded', 'aria-haspopup', 'role', 'tabindex'];
          this.triggerAttributes.set(trigger, attributes.map((name) => [name, trigger.getAttribute(name)]));
        }
        if (this.mobile.matches) {
          trigger.setAttribute('aria-controls', this.drawer.id || this.root.id);
          trigger.setAttribute('aria-expanded', String(this.isOpen));
          trigger.setAttribute('aria-haspopup', 'dialog');
          if (!trigger.matches('button, summary, a[href], input')) {
            trigger.setAttribute('role', 'button');
            trigger.setAttribute('tabindex', '0');
          }
        } else this.restoreTrigger(trigger);
      });
    }

    restoreTrigger(trigger) {
      this.triggerAttributes.get(trigger)?.forEach(([name, value]) => {
        if (value === null) trigger.removeAttribute(name);
        else trigger.setAttribute(name, value);
      });
    }

    menuEntries() {
      const template = this.root.querySelector('template[data-nova-mobile-tree]');
      const configured = distinctMenus(Array.from(template?.content.children || []).filter((node) => node.tagName === 'UL'));
      if (configured.length) return configured;
      const header = document.getElementById('header');
      if (!header) return [];
      const desktopLists = Array.from(header.querySelectorAll('ul.dt-desktop-menu[data-menu]')).filter((list) => list.dataset.menu === this.key);
      const entries = distinctMenus(desktopLists);
      const category = header.querySelector('.category-block__details-content');
      if (category && this.key === 'dt-main-menu') entries.push(...readList(category));
      if (entries.length) return entries;
      if (this.key !== 'dt-main-menu') return [];
      return readList(header.querySelector('.menu-drawer__menu'));
    }

    refresh() {
      this.close();
      this.panels.forEach(({ panel }) => panel.remove());
      this.panels.clear();
      this.serial = 0;
      this.rootList.replaceChildren();
      this.renderRows(this.rootList, this.menuEntries());
      this.syncTriggers();
    }

    link(entry, className, label = entry.label) {
      const link = element('a', className, label);
      link.setAttribute('href', entry.href);
      if (entry.current || this.isCurrent(entry.href)) link.setAttribute('aria-current', 'page');
      if (entry.target) link.setAttribute('target', entry.target);
      if (entry.rel) link.setAttribute('rel', entry.rel);
      if (entry.target === '_blank') link.setAttribute('rel', `${entry.rel} noopener noreferrer`.trim());
      return link;
    }

    isCurrent(href) {
      if (!destination(href)) return false;
      const url = new URL(href, location.href);
      return !url.hash && url.origin === location.origin && url.pathname === location.pathname && url.search === location.search;
    }

    renderRows(list, entries) {
      entries.forEach((entry) => {
        const row = element('li', 'row');
        const hasDestination = destination(entry.href);
        let toggle;
        if (hasDestination) row.append(this.link(entry, 'link'));
        if (entry.children.length) {
          toggle = element('button', hasDestination ? 'drill' : 'parent');
          toggle.type = 'button';
          if (!hasDestination) toggle.append(element('span', '', entry.label));
          toggle.append(arrow());
          toggle.setAttribute('aria-label', `${entry.label}: ${this.root.querySelector('.nova-mobile-navigation__root-label').textContent.trim()}`);
          toggle.setAttribute('aria-expanded', 'false');
          const panelId = `${this.root.id}-panel-${++this.serial}`;
          toggle.dataset.novaMobileDrill = panelId;
          toggle.setAttribute('aria-controls', panelId);
          row.append(toggle);
          this.buildPanel(entry, panelId, toggle);
        } else if (!hasDestination) {
          const label = element('span', 'link', entry.label);
          row.append(label);
        }
        list.append(row);
      });
    }

    buildPanel(entry, id, toggle) {
      const panel = element('div', 'panel');
      panel.id = id;
      panel.dataset.novaMobilePanel = id;
      panel.hidden = true;
      panel.inert = true;
      const heading = element('div', 'panel-heading');
      const back = element('button', 'back');
      back.type = 'button';
      back.dataset.novaMobileBack = '';
      back.setAttribute('aria-label', this.root.dataset.backLabel || 'Back');
      back.append(arrow());
      const title = element('h2', 'panel-title', entry.label);
      title.id = `${id}-title`;
      panel.setAttribute('aria-labelledby', title.id);
      heading.append(back, title);
      panel.append(heading);
      if (destination(entry.href)) {
        const all = this.link(entry, 'view-all', this.root.dataset.viewAllLabel || 'View all');
        all.setAttribute('aria-label', `${all.textContent}: ${entry.label}`);
        all.append(arrow());
        panel.append(all);
      }
      const list = element('ul', 'list');
      panel.append(list);
      this.panels.set(id, { panel, toggle, back });
      this.renderRows(list, entry.children);
      this.body.append(panel);
    }

    setPanel(panel, visible) {
      panel.hidden = !visible;
      panel.inert = !visible;
      panel.setAttribute('aria-hidden', String(!visible));
    }

    animatePanel(panel, backwards = false) {
      this.panelAnimation?.cancel();
      if (this.motion.matches || !panel.animate) return;
      this.panelAnimation = panel.animate({
        transform: [`translateX(${backwards ? '-12px' : '12px'})`, 'translateX(0)'],
        opacity: [0, 1]
      }, {duration: 220, easing: 'cubic-bezier(.22,.61,.36,1)'});
    }

    cancelDrawerMotion() {
      this.drawerAnimation?.cancel();
      this.backdropAnimation?.cancel();
      this.drawerAnimation = this.backdropAnimation = null;
    }

    animateDrawer(open, done = () => {}) {
      const continuing = Boolean(this.drawerAnimation);
      const fromTransform = getComputedStyle(this.drawer).transform;
      const fromOpacity = getComputedStyle(this.drawer).opacity;
      const backdrop = this.root.querySelector('.nova-mobile-navigation__backdrop');
      const fromBackdrop = backdrop ? getComputedStyle(backdrop).opacity : '1';
      this.cancelDrawerMotion();
      this.motionComplete = done;
      if (this.motion.matches || !this.drawer.animate) {
        this.motionComplete = null;
        done();
        return;
      }
      const options = {duration: open ? 300 : 220, easing: 'cubic-bezier(.22,.61,.36,1)', fill: 'both'};
      const animation = this.drawer.animate({
        transform: [open && !continuing ? 'translateX(-24px)' : fromTransform, open ? 'translateX(0)' : 'translateX(-24px)'],
        opacity: [open && !continuing ? 0 : fromOpacity, open ? 1 : 0]
      }, options);
      this.drawerAnimation = animation;
      if (backdrop) this.backdropAnimation = backdrop.animate({opacity: [open ? 0 : fromBackdrop, open ? 1 : 0]}, options);
      animation.onfinish = () => {
        if (this.drawerAnimation !== animation) return;
        this.cancelDrawerMotion();
        this.motionComplete = null;
        done();
      };
    }

    currentPanel() {
      return this.stack.at(-1)?.panel || this.rootPanel;
    }

    drill(id) {
      const entry = this.panels.get(id);
      if (!entry || !this.currentPanel().contains(entry.toggle)) return;
      entry.previousScroll = this.body.scrollTop;
      this.setPanel(this.currentPanel(), false);
      entry.toggle.setAttribute('aria-expanded', 'true');
      this.stack.push(entry);
      this.setPanel(entry.panel, true);
      this.animatePanel(entry.panel);
      this.body.scrollTop = 0;
      entry.back.focus({ preventScroll: true });
    }

    back() {
      const entry = this.stack.pop();
      if (!entry) return;
      this.setPanel(entry.panel, false);
      entry.toggle.setAttribute('aria-expanded', 'false');
      this.setPanel(this.currentPanel(), true);
      this.animatePanel(this.currentPanel(), true);
      this.body.scrollTop = entry.previousScroll;
      entry.toggle.focus({ preventScroll: true });
    }

    resetPanels() {
      this.stack = [];
      this.panels.forEach(({ panel, toggle }) => {
        this.setPanel(panel, false);
        toggle.setAttribute('aria-expanded', 'false');
      });
      this.setPanel(this.rootPanel, true);
      this.body.scrollTop = 0;
    }

    lockBackground() {
      const body = document.body;
      const html = document.documentElement;
      const properties = ['position', 'top', 'left', 'right', 'width', 'overflow', 'padding-right'];
      this.scroll = { x: window.scrollX, y: window.scrollY };
      this.bodyStyles = properties.map((name) => [name, body.style.getPropertyValue(name), body.style.getPropertyPriority(name)]);
      this.htmlStyles = ['overflow', 'scroll-behavior'].map((name) => [name, html.style.getPropertyValue(name), html.style.getPropertyPriority(name)]);
      const scrollbar = Math.max(0, window.innerWidth - html.clientWidth);
      const padding = parseFloat(getComputedStyle(body).paddingRight) || 0;
      body.style.setProperty('position', 'fixed', 'important');
      body.style.setProperty('top', `${-this.scroll.y}px`, 'important');
      body.style.setProperty('left', `${-this.scroll.x}px`, 'important');
      body.style.setProperty('right', '0', 'important');
      body.style.setProperty('width', '100%', 'important');
      body.style.setProperty('overflow', 'hidden', 'important');
      if (scrollbar) body.style.setProperty('padding-right', `${padding + scrollbar}px`, 'important');
      html.style.setProperty('overflow', 'hidden', 'important');
      for (let branch = this.root; branch && branch !== body; branch = branch.parentElement) {
        Array.from(branch.parentElement.children).forEach((sibling) => {
          if (sibling !== branch && !['SCRIPT', 'STYLE', 'LINK', 'TEMPLATE'].includes(sibling.tagName)) {
            this.background.set(sibling, sibling.hasAttribute('inert'));
            sibling.inert = true;
          }
        });
      }
    }

    unlockBackground() {
      this.background.forEach((wasInert, node) => {
        node.inert = wasInert;
        if (!wasInert) node.removeAttribute('inert');
      });
      this.background.clear();
      const restore = (node, properties) => properties.forEach(([name, value, priority]) => {
        if (value) node.style.setProperty(name, value, priority);
        else node.style.removeProperty(name);
      });
      restore(document.body, this.bodyStyles);
      document.documentElement.style.setProperty('scroll-behavior', 'auto', 'important');
      window.scrollTo(this.scroll.x, this.scroll.y);
      restore(document.documentElement, this.htmlStyles);
    }

    open(trigger) {
      if (!this.mobile.matches || this.isOpen || !this.rootList.children.length) return;
      controllers.forEach((controller) => { if (controller !== this) controller.close(false); });
      this.opener = trigger;
      this.resetPanels();
      this.root.hidden = false;
      this.root.inert = false;
      this.root.setAttribute('aria-hidden', 'false');
      this.root.dataset.open = '';
      this.isOpen = true;
      this.lockBackground();
      this.syncTriggers();
      this.closeButton.focus({ preventScroll: true });
      this.animateDrawer(true);
    }

    close(returnFocus = true) {
      if (!this.isOpen) return;
      this.isOpen = false;
      this.unlockBackground();
      this.root.inert = true;
      this.root.setAttribute('aria-hidden', 'true');
      delete this.root.dataset.open;
      this.syncTriggers();
      if (returnFocus && this.opener?.isConnected) this.opener.focus({ preventScroll: true });
      this.opener = null;
      this.animateDrawer(false, () => {
        if (this.isOpen) return;
        this.root.hidden = true;
        this.resetPanels();
      });
    }

    onDocumentClick(event) {
      if (!this.mobile.matches || !(event.target instanceof Element)) return;
      const trigger = event.target.closest('.menu-trigger[data-menu], #header summary.header__icon--menu');
      if (trigger && this.triggers().includes(trigger)) {
        if (!this.rootList.children.length) this.refresh();
        if (!this.rootList.children.length) return;
        event.preventDefault();
        event.stopImmediatePropagation();
        if (this.isOpen) this.close();
        else this.open(trigger);
      } else if (this.isOpen && !this.root.contains(event.target)) {
        event.preventDefault();
        event.stopImmediatePropagation();
        this.close();
      }
    }

    onDrawerClick(event) {
      if (!this.isOpen || !(event.target instanceof Element)) return;
      if (event.target.closest('[data-nova-mobile-close]')) this.close();
      else if (event.target.closest('[data-nova-mobile-back]')) this.back();
      else {
        const toggle = event.target.closest('[data-nova-mobile-drill]');
        if (toggle) this.drill(toggle.dataset.novaMobileDrill);
        else if (event.target.closest('a[href]')) this.close(false);
      }
    }

    focusable() {
      return Array.from(this.drawer.querySelectorAll('a[href], button, [tabindex="0"]')).filter((node) => {
        return !node.disabled && !node.closest('[hidden], [inert]');
      });
    }

    onKeyDown(event) {
      if (!this.mobile.matches || !(event.target instanceof Element)) return;
      const trigger = event.target.closest('.menu-trigger[data-menu], #header summary.header__icon--menu');
      if (!this.isOpen && trigger && this.triggers().includes(trigger) && ['Enter', ' '].includes(event.key)) {
        if (!this.rootList.children.length) return;
        event.preventDefault();
        event.stopImmediatePropagation();
        this.open(trigger);
        return;
      }
      if (!this.isOpen) return;
      if (event.key === 'Escape' || (event.key === 'ArrowLeft' && this.stack.length)) {
        event.preventDefault();
        event.stopImmediatePropagation();
        if (event.key === 'Escape') this.close();
        else this.back();
      } else if (event.key === 'Tab') {
        const targets = this.focusable();
        const first = targets[0] || this.drawer;
        const last = targets.at(-1) || this.drawer;
        if (event.shiftKey && (event.target === first || !targets.includes(event.target))) {
          event.preventDefault();
          last.focus({ preventScroll: true });
        } else if (!event.shiftKey && (event.target === last || !targets.includes(event.target))) {
          event.preventDefault();
          first.focus({ preventScroll: true });
        }
      }
    }

    destroy() {
      this.close();
      this.cancelDrawerMotion();
      this.panelAnimation?.cancel();
      this.root.hidden = true;
      this.abort.abort();
      this.triggerAttributes.forEach((_, trigger) => this.restoreTrigger(trigger));
    }
  }

  function mount() {
    controllers.forEach((controller, root) => {
      if (!root.isConnected) {
        controller.destroy();
        controllers.delete(root);
      }
    });
    document.querySelectorAll(rootSelector).forEach((root) => {
      if (!controllers.has(root)) controllers.set(root, new NovaMobileNavigation(root));
    });
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', mount, { once: true });
  else mount();
  document.addEventListener('shopify:section:load', () => {
    mount();
    controllers.forEach((controller) => controller.refresh());
  });
  document.addEventListener('shopify:section:unload', (event) => {
    controllers.forEach((controller, root) => {
      if (event.target.contains(root) || event.target.id === `shopify-section-${root.dataset.sourceSectionId}`) {
        controller.destroy();
        controllers.delete(root);
        root.remove();
      } else if (event.target.contains(document.getElementById('header'))) controller.close();
    });
  });
  window.addEventListener('pagehide', () => controllers.forEach((controller) => controller.close(false)));
})();
